import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { isValidPhoneNumber } from "libphonenumber-js";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ConfirmationIcon } from "../../../../assets/icons/Confirmation.svg";
import { ReactComponent as SuccessIcon } from "../../../../assets/Images/SuccessProjectIcon.svg";
import { commonStyles } from "../../../../assets/MUIStyles/commonStyles";
import { layoutStyles } from "../../../../assets/MUIStyles/mainLayout";
import BaseButton from "../../Common/Buttons/BaseButton";
import CommonPopup from "../../CommonComponents/PopUpCards/CommonPopup";
import ErrorPopUp from "../../CommonComponents/PopUpCards/ErrorPopUp";
import {
  generateVerificationCodeService,
  verifyVerificationCodeService,
} from "../../services/commonServices";

const UpdateProfile = ({
  open,
  formData,
  setFormData,
  handleModalClose,
  updateUserDetails,
}) => {
  const theme = useTheme();
  const brandColor = theme.custom.brandColor;
  const [isVisible, setIsVisible] = useState(true);
  const [updateButtonLoading, setUpdateButtonLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { firstName, lastName, phone, email, address, city, pincode } =
    formData;

  const [warningPopup, setWarningPopup] = useState({
    open: false,
    errMsg: "",
  });

  const [initialPhone, setInitialPhone] = useState(formData?.phone || "");
  const [otpModal, setOtpModal] = useState({
    open: false,
    code: "",
    loading: false,
    sentTo: "",
  });
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [otpFeedbackPopup, setOtpFeedbackPopup] = useState({
    open: false,
    type: "success" as "success" | "error",
    message: "",
  });

  useEffect(() => {
    if (open) {
      setInitialPhone(formData?.phone || "");
      setVerifiedPhone("");
      setOtpModal({ open: false, code: "", loading: false, sentTo: "" });
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["phone", "pincode"].includes(name)) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value.replace(/[a-zA-Z]/g, ""),
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const checkWhiteSpace = (str) => !str || !str.trim();

  const normalizePhone = (p) => (p || "").trim();
  const hasPhoneChanged =
    normalizePhone(phone) && normalizePhone(phone) !== normalizePhone(initialPhone);

  const sendOtpForCurrentPhone = async () => {
    setOtpModal((prev) => ({ ...prev, loading: true, sentTo: "" }));
    const res: any = await generateVerificationCodeService({
      phoneNumber: normalizePhone(phone),
    });
    if (res?.status === 200) {
      setOtpModal((prev) => ({
        ...prev,
        loading: false,
        sentTo: normalizePhone(phone),
      }));
      setOtpFeedbackPopup({
        open: true,
        type: "success",
        message: "OTP has been sent successfully.",
      });
      return;
    }
    setOtpModal((prev) => ({ ...prev, loading: false }));
    setOtpFeedbackPopup({
      open: true,
      type: "error",
      message:
        res?.errMsg || "We couldn’t send an OTP right now. Please try again.",
    });
  };

  const openOtpModal = async () => {
    setOtpModal((prev) => ({ ...prev, open: true }));
    await sendOtpForCurrentPhone();
  };

  const verifyOtpAndUpdate = async () => {
    if (otpModal.code.trim().length < 4) {
      setWarningPopup({
        open: true,
        errMsg: "Please enter a valid OTP.",
      });
      return;
    }
    setOtpModal((prev) => ({ ...prev, loading: true }));
    const res: any = await verifyVerificationCodeService({
      otp: otpModal.code.trim(),
    });
    if (res?.status === 200) {
      setVerifiedPhone(normalizePhone(phone));
      setInitialPhone(normalizePhone(phone));
      setOtpModal({ open: false, code: "", loading: false, sentTo: "" });
      setOtpFeedbackPopup({
        open: true,
        type: "success",
        message: "OTP verified successfully.",
      });
      await updateUserDetails(formData);
      setUpdateButtonLoading(false);
      return;
    }
    setOtpModal((prev) => ({ ...prev, loading: false }));
    setOtpFeedbackPopup({
      open: true,
      type: "error",
      message: res?.errMsg || "OTP verification failed. Please try again.",
    });
  };

  const handleSubmit = async () => {
    setUpdateButtonLoading(true);

    // OTP already sent: do not send again or save profile until user verifies.
    if (
      otpModal.open &&
      hasPhoneChanged &&
      verifiedPhone !== normalizePhone(phone)
    ) {
      setWarningPopup({
        open: true,
        errMsg:
          "Please enter the OTP sent to your phone. Your number will be updated only after verification.",
      });
      setUpdateButtonLoading(false);
      return;
    }

    const obj = {
      firstName,
      lastName,
      phone,
      email,
      city,
      address,
      pincode,
    };

    for (let key in obj) {
      if (checkWhiteSpace(obj[key])) {
        setWarningPopup({
          open: true,
          errMsg: "All fields are required.",
        });
        setUpdateButtonLoading(false);
        return;
      }
    }

    if (!isValidPhoneNumber(phone)) {
      setWarningPopup({
        open: true,
        errMsg: "Please enter a valid phone number with country code.",
      });
      setUpdateButtonLoading(false);
      return;
    }

    if (hasPhoneChanged && verifiedPhone !== normalizePhone(phone)) {
      await openOtpModal();
      setUpdateButtonLoading(false);
      return;
    }

    await updateUserDetails(formData);
    setInitialPhone(normalizePhone(phone));
    setUpdateButtonLoading(false);
  };

  const handleClose = () => {
    setIsVisible(false);
    handleModalClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (warningPopup.open) {
        return;
      }

      if (otpModal.open) {
        return;
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [warningPopup.open, otpModal.open]);

  return createPortal(
    <Box
      ref={containerRef}
      sx={{
        ...layoutStyles.profileUpdateContainer,
        left: open ? "280px" : "90px",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
      }}
    >
      <Box sx={layoutStyles.profileInnerContainer}>
        <Avatar
          sx={{ ...layoutStyles.profileAvatar, backgroundColor: brandColor }}
        >
          {formData.email?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Typography className="font-16-600-grey">
          {formData.firstName} {formData.lastName}
        </Typography>
        <Typography className="font-16-grey" gutterBottom>
          {formData.email}
        </Typography>
      </Box>
      <Box sx={layoutStyles.profileContentContainer}>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">First Name</Typography>
            <TextField
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your first name"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">Last Name</Typography>
            <TextField
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your last name"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Email</Typography>
          <TextField
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="mailid@gmail.com"
            sx={commonStyles.inputField}
            disabled
            slotProps={{
              htmlInput: { maxLength: 50 },
            }}
          />
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Phone number</Typography>
          <TextField
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your Phone Number"
            sx={commonStyles.inputField}
            slotProps={{
              htmlInput: { maxLength: 18 },
            }}
          />
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Address</Typography>
          <TextField
            name="address"
            value={formData.address}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your address here"
            sx={commonStyles.inputField}
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">City</Typography>
            <TextField
              name="city"
              value={formData.city}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your city"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 35 },
              }}
            />
          </Box>
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">Pincode</Typography>
            <TextField
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your Pincode"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={layoutStyles.profileButtonCont}>
        <Button onClick={handleClose} sx={commonStyles.clearButton}>
          Close
        </Button>
        <BaseButton
          variant="contained"
          title="Update profile"
          loading={updateButtonLoading}
          handleClick={handleSubmit}
          disabled={
            otpModal.open &&
            hasPhoneChanged &&
            verifiedPhone !== normalizePhone(phone)
          }
        />
      </Box>
      {warningPopup.open && (
        <ErrorPopUp
          show={warningPopup.open}
          headerText="Warning"
          toggle={() =>
            setWarningPopup((prev) => ({
              ...prev,
              open: false,
              errMsg: "",
            }))
          }
          heading={"Warning"}
          message={warningPopup.errMsg}
          spin={false}
          Icon={ConfirmationIcon}
        />
      )}

      {otpFeedbackPopup.open && otpFeedbackPopup.type === "success" && (
        <CommonPopup
          message={otpFeedbackPopup.message}
          show={otpFeedbackPopup.open}
          toggle={() =>
            setOtpFeedbackPopup({ open: false, type: "success", message: "" })
          }
          Icon={SuccessIcon}
          heading=""
        />
      )}

      {otpFeedbackPopup.open && otpFeedbackPopup.type === "error" && (
        <ErrorPopUp
          show={otpFeedbackPopup.open}
          headerText="OTP"
          toggle={() =>
            setOtpFeedbackPopup({ open: false, type: "error", message: "" })
          }
          heading={"Error"}
          message={otpFeedbackPopup.message}
          spin={false}
        />
      )}

      <Dialog
        open={otpModal.open}
        onClose={() =>
          setOtpModal((prev) => ({ ...prev, open: false, code: "" }))
        }
        classes={{ paper: "common-modal-450" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              maxWidth: 450,
              width: "100%",
            },
          },
        }}
      >
        <DialogTitle>Verify phone number</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography className="font-14-lightgrey" sx={{ mb: 2 }}>
            Enter the OTP sent to <b>{otpModal.sentTo || normalizePhone(phone)}</b>.
          </Typography>
          <TextField
            value={otpModal.code}
            onChange={(e) =>
              setOtpModal((prev) => ({
                ...prev,
                code: e.target.value.replace(/\D/g, "").slice(0, 6),
              }))
            }
            variant="outlined"
            fullWidth
            placeholder="Enter OTP"
            sx={commonStyles.inputField}
            disabled={otpModal.loading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setOtpModal((prev) => ({ ...prev, open: false, code: "" }))
            }
            disabled={otpModal.loading}
            sx={commonStyles.clearButton}
          >
            Cancel
          </Button>
          <Button
            onClick={() => sendOtpForCurrentPhone()}
            disabled={otpModal.loading}
            sx={commonStyles.clearButton}
          >
            Resend OTP
          </Button>
          <BaseButton
            variant="contained"
            title="Verify & Update"
            loading={otpModal.loading}
            handleClick={verifyOtpAndUpdate}
          />
        </DialogActions>
      </Dialog>
    </Box>,
    document.body
  );
};

export default UpdateProfile;
